/**
 * Tile identity, tile extents, and the conversions between a tile and the local
 * ENU frame.
 */

import type { EnuPoint, GeoOrigin, GeoPoint } from './enu.js';
import { geoToEnu } from './enu.js';
import {
  latToTileY,
  lonToTileX,
  mercatorYToLat,
  tileXToLon,
  tilesPerAxis,
  tileYToLat,
} from './mercator.js';

export interface TileKey {
  z: number;
  x: number;
  y: number;
}

/** Geographic extent of a tile. North > south; west < east. */
export interface TileBounds {
  west: number;
  east: number;
  north: number;
  south: number;
}

/** Axis-aligned extent of a tile in the local ENU frame, in metres. */
export interface TileExtent {
  minEast: number;
  maxEast: number;
  minNorth: number;
  maxNorth: number;
}

export function tileKey(z: number, x: number, y: number): TileKey {
  return { z, x, y };
}

/** Canonical string form, `z/x/y`. Used as cache and map keys. */
export function tileKeyToString(key: TileKey): string {
  return `${key.z}/${key.x}/${key.y}`;
}

export function parseTileKey(s: string): TileKey {
  const parts = s.split('/');
  if (parts.length !== 3) throw new Error(`Malformed tile key: ${s}`);
  const [z, x, y] = parts.map(Number);
  if (!Number.isInteger(z) || !Number.isInteger(x) || !Number.isInteger(y)) {
    throw new Error(`Malformed tile key: ${s}`);
  }
  return { z, x, y };
}

export function tileKeyEquals(a: TileKey, b: TileKey): boolean {
  return a.z === b.z && a.x === b.x && a.y === b.y;
}

/** Wrap a column index around the antimeridian. Rows are not wrapped. */
export function wrapTileX(x: number, z: number): number {
  const n = tilesPerAxis(z);
  return ((x % n) + n) % n;
}

/** A row outside [0, 2^z) is off the top or bottom of the world and cannot exist. */
export function isTileRowInRange(y: number, z: number): boolean {
  return y >= 0 && y < tilesPerAxis(z);
}

/** The tile containing a geographic point. */
export function tileAt(lat: number, lon: number, z: number): TileKey {
  return { z, x: lonToTileX(lon, z), y: latToTileY(lat, z) };
}

export function tileBounds(key: TileKey): TileBounds {
  return {
    west: tileXToLon(key.x, key.z),
    east: tileXToLon(key.x + 1, key.z),
    north: tileYToLat(key.y, key.z),
    south: tileYToLat(key.y + 1, key.z),
  };
}

export function tileCentre(key: TileKey): GeoPoint {
  const b = tileBounds(key);
  // Centre in mercator Y, then project back — not the mean of the two
  // latitudes, which would be biased toward the equator.
  const midMercY = (key.y + 0.5) / tilesPerAxis(key.z);
  return { lat: mercatorYToLat(midMercY), lon: (b.west + b.east) / 2 };
}

/**
 * Latitudes of `count` evenly spaced sample rows spanning the tile, north to
 * south, inclusive of both edges.
 *
 * The rows are spaced evenly in *mercator Y*, then projected to latitude. This
 * is the whole point of the function: a DEM tile's pixel rows are evenly spaced
 * in mercator Y, not in latitude. Dividing the tile's north-south extent
 * linearly instead would shear the terrain by a few metres per tile — small,
 * but it does not cancel between neighbouring tiles, so it shows up as a
 * persistent ridge along every tile seam.
 */
export function tileSampleLatitudes(key: TileKey, count: number): Float64Array {
  const out = new Float64Array(count);
  const n = tilesPerAxis(key.z);
  const step = 1 / (count - 1);
  for (let i = 0; i < count; i++) {
    out[i] = mercatorYToLat((key.y + i * step) / n);
  }
  return out;
}

/** Longitudes of `count` evenly spaced sample columns, west to east, inclusive. */
export function tileSampleLongitudes(key: TileKey, count: number): Float64Array {
  const out = new Float64Array(count);
  const n = tilesPerAxis(key.z);
  const step = 1 / (count - 1);
  for (let i = 0; i < count; i++) {
    out[i] = (key.x + i * step) / n * 360 - 180;
  }
  return out;
}

/** The tile's extent in the local ENU frame. */
export function tileExtent(key: TileKey, origin: GeoOrigin): TileExtent {
  const b = tileBounds(key);
  const nw = geoToEnu(origin, b.north, b.west);
  const se = geoToEnu(origin, b.south, b.east);
  return {
    minEast: nw.east,
    maxEast: se.east,
    minNorth: se.north,
    maxNorth: nw.north,
  };
}

/**
 * Shortest planar distance from a point to a tile's rectangle, in metres.
 * Zero when the point is inside.
 *
 * Streaming priority uses this rather than distance to the tile centre: a tile
 * the player is standing at the edge of must outrank a tile whose centre
 * happens to be marginally nearer.
 */
export function distanceToTile(extent: TileExtent, point: EnuPoint): number {
  const dx = Math.max(extent.minEast - point.east, 0, point.east - extent.maxEast);
  const dy = Math.max(extent.minNorth - point.north, 0, point.north - extent.maxNorth);
  return Math.hypot(dx, dy);
}

export function parentTile(key: TileKey): TileKey | null {
  if (key.z === 0) return null;
  return { z: key.z - 1, x: key.x >> 1, y: key.y >> 1 };
}

/**
 * The tile at `z` covering the same ground as `key`.
 * Zooming in returns the tile containing `key`'s north-west corner.
 */
export function tileAtZoom(key: TileKey, z: number): TileKey {
  const shift = z - key.z;
  if (shift === 0) return { ...key };
  if (shift < 0) return { z, x: key.x >> -shift, y: key.y >> -shift };
  return { z, x: key.x << shift, y: key.y << shift };
}

export function childTiles(key: TileKey): TileKey[] {
  const z = key.z + 1;
  const x = key.x * 2;
  const y = key.y * 2;
  return [
    { z, x, y },
    { z, x: x + 1, y },
    { z, x, y: y + 1 },
    { z, x: x + 1, y: y + 1 },
  ];
}
